/**
 * Does a document line's own description earn a second line on the paper?
 *
 * Usually it does not. The rep app composes `description` as
 * `"<item name> · <unit>"` (mandob-mobile/src/lib/cartStore.ts), so the printed
 * row showed the product name, then showed it AGAIN underneath with the
 * packaging appended — beside a الوحدة column already stating that packaging. On
 * an Arabic order that reads as the same line printed twice, which is exactly
 * how it was reported.
 *
 * The old guard was `description !== title && description !== itemName`: exact
 * equality, so anything with so much as a unit suffix slipped through.
 *
 * Lives in its own module so the rule can be tested against the real strings
 * rather than only through a rendered page.
 */

/** Strip separators, case and combining forms so two spellings of one thing match. */
export function squashLabel(v: string): string {
  return v.normalize('NFKC').replace(/[\s·•.,\-–—_|/\\]+/gu, '').toLowerCase();
}

export interface DescribableLine {
  description?: string | null;
  itemName?: string | null;
  unitName?: string | null;
  itemUom?: string | null;
}

/**
 * True when the description says nothing the row does not already say.
 *
 * `title` is the name AS DISPLAYED, which on an English sheet is the translation
 * while the description is still Arabic — so both it and the stored `itemName`
 * are compared. A trailing unit word is removed first, because the الوحدة column
 * prints it in its own right.
 */
export function descriptionAddsNothing(line: DescribableLine, title: string): boolean {
  const raw = (line.description ?? '').trim();
  if (!raw) return true;
  let d = squashLabel(raw);
  if (!d) return true;

  for (const u of [line.unitName, line.itemUom]) {
    const su = u ? squashLabel(u) : '';
    if (su && d.endsWith(su)) {
      d = d.slice(0, -su.length);
      break;
    }
  }

  return [line.itemName, title]
    .filter((n): n is string => Boolean(n))
    .map(squashLabel)
    .some((n) => n.length > 0 && d === n);
}
